// src/support/support.client.ts — panoul „Ajutor” (F14-T2/T6): o singură ușă pentru identitățile de
// LUCRU (membru al unei primării sau rol de platformă). Trei lucruri într-un loc: căutarea în ghid
// (atlasul UI, fără AI — secțiuni cu captură, chenare și link „Deschide”), firele către echipa de
// suport (tichetele) și, când instanța are asistent configurat (`/api/support/config`), conversația
// cu asistentul: bulele lui, CARDURILE secțiunilor pe care le citează (modelul dă doar id-uri — D11 —,
// aplicația randează captura, titlul și linkul), întrebarea „s-a rezolvat?” la al 3-lea răspuns și
// butonul „vreau un om”. Fără asistent, mesajul ajunge direct la echipă — panoul o spune.
// Aceeași interfață e și pagina `#/suport` (deep-link-ul din emailul de răspuns: `#/suport?t=<id>`).
//
// Reguli Van respectate: panoul e montat O DATĂ (pe body) și comutat prin class; textul din câmpuri
// stă în variabile de MODUL, nu în store (o citire reactivă în corpul formularului pierdea focusul la
// fiecare literă — lecția T5 colectare); funcțiile reactive nu întorc niciodată null.
// HTML-ul secțiunilor vine din atlasul generat din manuale (sursă de încredere) → innerHTML;
// mesajele oamenilor și ale asistentului sunt mereu noduri text.

import "../styles.css"
import van from "vanjs-core"
import * as vanX from "vanjs-ext"
import { api_get, api_post } from "../lib/api.client"
import { session } from "../lib/session.client"
import { workspace } from "../lib/workspace.client"
import { get_hash_query_params, navigate } from "../lib/router.client"
import { collect_context } from "../lib/support_context.client"
import {
	SUPPORT_STATUS_LABEL_RO,
	SUPPORT_KIND_LABEL_RO,
	MAX_SUPPORT_BODY,
	type SupportStatus,
} from "../lib/support"
import type { HelpBox, HelpSection } from "../lib/help_atlas"
import type { HelpSearch, HelpSearchResult } from "../help"
import type { SupportThread, SupportThreadDetail, SupportPosted, SupportMessage } from "../support"

const t = van.tags

/** D7: panoul e doar pentru conturile de lucru. */
export function has_work_identity(): boolean {
	const s = session.val
	return s.authenticated && (s.memberships.length > 0 || !!s.user?.platform_role)
}

type View = "home" | "section" | "thread"
interface AiConfig {
	loaded: boolean
	assistant: boolean
	max_replies: number
	mail_users: boolean // răspunsul operatorului ajunge și pe email (altfel textele nu-l promit)
	cooldown_hours: number
}
const S = vanX.reactive({
	open: false,
	view: "home" as View,
	threads: [] as SupportThread[],
	threads_loaded: false,
	thread: null as SupportThreadDetail | null,
	results: [] as HelpSearchResult[],
	searched: false,
	section: null as HelpSection | null,
	busy: false,
	error: "",
	notice: "",
	ai: {
		loaded: false,
		assistant: false,
		max_replies: 3,
		cooldown_hours: 4,
		mail_users: true,
	} as AiConfig,
})
// textul câmpurilor — în MODUL, nu în store (focusul)
let draft_new = ""
let draft_reply = ""
let search_q = ""
let search_timer: ReturnType<typeof setTimeout> | null = null
let search_seq = 0
// secțiunile citate de asistent, memorate per id (cardurile se randează imperativ, nu reactiv)
const section_cache = new Map<string, Promise<HelpSection | null>>()

const BTN =
	"px-3 py-1.5 rounded-lg bg-cyan-700 text-white text-sm font-semibold hover:bg-cyan-800 cursor-pointer disabled:opacity-60"
const BTN_GHOST =
	"px-2.5 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer disabled:opacity-60"
const INPUT =
	"w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm outline-none focus:border-cyan-500"
const CARD = "rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"

function fmt_date(iso: string | null): string {
	if (!iso) {
		return "—"
	}
	const d = new Date(iso)
	return isNaN(d.getTime())
		? "—"
		: d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })
}
const STATUS_CLS: Record<SupportStatus, string> = {
	chat: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300",
	escalated: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
	answered: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300",
	closed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
}
const STATUS_LABEL: Record<SupportStatus, string> = {
	...SUPPORT_STATUS_LABEL_RO,
	chat: "Cu asistentul",
}
export function status_badge(status: SupportStatus) {
	return t.span(
		{
			class: `inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[status] ?? STATUS_CLS.chat}`,
		},
		STATUS_LABEL[status] ?? status,
	)
}
/** Al 3-lea răspuns al asistentului: firul așteaptă „s-a rezolvat?” (stare DERIVATĂ, ca pe server). */
const awaiting_resolution = (th: SupportThread): boolean =>
	th.status === "chat" && th.ai_replies >= S.ai.max_replies

// ── date ──────────────────────────────────────────────────────────────────────────────────────
async function load_config(): Promise<void> {
	if (S.ai.loaded) {
		return
	}
	const r = await api_get<{
		assistant: boolean
		max_replies: number
		cooldown_hours: number
		mail_users: boolean
	}>("/api/support/config")
	S.ai = r.ok ? { loaded: true, ...r.data } : { ...S.ai, loaded: true }
}
async function load_threads(): Promise<void> {
	const r = await api_get<SupportThread[]>("/api/support/threads")
	if (r.ok) {
		S.threads = r.data
	}
	S.threads_loaded = true
}
async function open_thread(id: string): Promise<void> {
	S.error = ""
	const r = await api_get<SupportThreadDetail>(`/api/support/threads/${encodeURIComponent(id)}`)
	if (!r.ok) {
		S.error = r.err?.message ?? "Firul nu e disponibil."
		return
	}
	S.thread = r.data
	S.view = "thread"
}
function upsert_thread(th: SupportThread): void {
	const i = S.threads.findIndex((x) => x.id === th.id)
	if (i >= 0) {
		S.threads[i] = th
	} else {
		S.threads.unshift(th)
	}
}
async function send_new(): Promise<void> {
	const body = draft_new.trim()
	if (!body || S.busy) {
		return
	}
	S.busy = true
	S.error = ""
	S.notice = ""
	const r = await api_post<SupportThreadDetail>("/api/support/threads", {
		body,
		context: collect_context(),
		...(workspace.val.slug ? { map: workspace.val.slug } : {}),
	})
	S.busy = false
	if (!r.ok) {
		S.error = r.err?.message ?? "Trimiterea a eșuat."
		return
	}
	draft_new = ""
	upsert_thread(r.data.thread)
	S.thread = r.data
	S.view = "thread"
	if (r.data.thread.status === "escalated") {
		S.notice = `Mesajul a ajuns la echipa de suport. ${answer_channel()}`
	}
}
async function send_reply(): Promise<void> {
	const d = S.thread
	const body = draft_reply.trim()
	if (!d || !body || S.busy) {
		return
	}
	S.busy = true
	S.error = ""
	S.notice = ""
	const r = await api_post<SupportPosted>(
		`/api/support/threads/${encodeURIComponent(d.thread.id)}/messages`,
		{ body },
	)
	S.busy = false
	if (!r.ok) {
		S.error = r.err?.message ?? "Trimiterea a eșuat."
		return
	}
	draft_reply = ""
	upsert_thread(r.data.thread)
	await open_thread(d.thread.id) // mesajele asistentului/sistemului ale tranziției
}
async function resolve_current(): Promise<void> {
	const d = S.thread
	if (!d || S.busy) {
		return
	}
	S.busy = true
	const r = await api_post<SupportThread>(
		`/api/support/threads/${encodeURIComponent(d.thread.id)}/resolve`,
		{},
	)
	S.busy = false
	if (r.ok) {
		upsert_thread(r.data)
		await open_thread(d.thread.id)
		S.notice = "Mulțumim! Firul e închis; îl poți redeschide oricând cu un mesaj nou."
	} else {
		S.error = r.err?.message ?? "Nu s-a putut închide."
	}
}
/** „Vreau să vorbesc cu cineva” — firul pleacă la echipă acum, fără alt răspuns al asistentului. */
async function escalate_current(): Promise<void> {
	const d = S.thread
	if (!d || S.busy) {
		return
	}
	S.busy = true
	const r = await api_post<SupportThread>(
		`/api/support/threads/${encodeURIComponent(d.thread.id)}/escalate`,
		{},
	)
	S.busy = false
	if (r.ok) {
		upsert_thread(r.data)
		await open_thread(d.thread.id)
		S.notice = `Firul a ajuns la echipa de suport. ${answer_channel()}`
	} else {
		S.error = r.err?.message ?? "Nu s-a putut trimite."
	}
}
function run_search(q: string): void {
	const seq = ++search_seq
	if (q.trim().length < 2) {
		S.results = []
		S.searched = false
		return
	}
	const ws = workspace.val.slug
	void api_get<HelpSearch>(
		`/api/support/help/search?q=${encodeURIComponent(q.trim())}${ws ? `&map=${encodeURIComponent(ws)}` : ""}`,
	).then((r) => {
		if (seq !== search_seq) {
			return // răspuns întârziat pentru alt text
		}
		S.results = r.ok ? r.data.results : []
		S.searched = true
	})
}
function ensure_section(id: string): Promise<HelpSection | null> {
	let p = section_cache.get(id)
	if (!p) {
		p = api_get<HelpSection>(`/api/support/help/sections/${encodeURIComponent(id)}`).then((r) =>
			r.ok ? r.data : null,
		)
		section_cache.set(id, p)
	}
	return p
}
async function open_section(id: string): Promise<void> {
	S.error = ""
	const sec = await ensure_section(id)
	if (!sec) {
		S.error = "Secțiunea nu e disponibilă."
		return
	}
	S.section = sec
	S.view = "section"
}

/** Deschide panoul (opțional direct pe un fir). */
export function open_help(thread_id?: string): void {
	S.open = true
	S.error = ""
	void load_config()
	if (!S.threads_loaded) {
		void load_threads()
	}
	if (thread_id) {
		void open_thread(thread_id)
	}
}
export function close_help(): void {
	S.open = false
}

// ── piese de UI ───────────────────────────────────────────────────────────────────────────────
/** Captura cu chenarele numerotate, ca în manual (poziții în % din imagine). */
/**
 * Captura mărită (feedback Ioana 08.10: „să poată deschide poza mai mare, să vadă exact ce e încercuit”):
 * un strat peste tot ecranul, DEASUPRA panoului (z-[60] > z-50), cu aceeași captură și aceleași chenare
 * numerotate, la lățimea ecranului. Montat O DATĂ pe body la prima deschidere și comutat prin class
 * (regula Van: nimic nu se detașează); închis cu ✕, click pe fundal sau Esc.
 */
const LIGHTBOX = van.state<{ poza: string; boxes: HelpBox[] } | null>(null)
let lightbox_mounted = false
function ensure_lightbox(): void {
	if (lightbox_mounted) {
		return
	}
	lightbox_mounted = true
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && LIGHTBOX.val) {
			LIGHTBOX.val = null
		}
	})
	document.body.appendChild(
		t.div(
			{
				class: () =>
					"fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3 sm:p-6" +
					(LIGHTBOX.val ? "" : " hidden"),
				onclick: (e: MouseEvent) => {
					if (e.target === e.currentTarget) {
						LIGHTBOX.val = null
					}
				},
			},
			t.button(
				{
					class:
						"absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 text-slate-900 text-lg font-bold cursor-pointer hover:bg-white",
					title: "Închide (Esc)",
					onclick: () => {
						LIGHTBOX.val = null
					},
				},
				"✕",
			),
			t.div({ class: "w-full max-w-[1400px] max-h-[92vh] overflow-auto" }, () =>
				LIGHTBOX.val ? shot_view(LIGHTBOX.val.poza, LIGHTBOX.val.boxes, false, false) : t.span(),
			),
		),
	)
}
function open_lightbox(poza: string, boxes: HelpBox[]): void {
	ensure_lightbox()
	LIGHTBOX.val = { poza, boxes }
}
/** Captura cu chenarele numerotate; `zoomable` = click → captura mărită (în lightbox e oprit). */
function shot_view(poza: string, boxes: HelpBox[], small = false, zoomable = true) {
	return t.div(
		{
			class: (small ? "relative" : "relative my-2") + (zoomable ? " cursor-zoom-in" : ""),
			...(zoomable
				? {
						title: "Mărește captura",
						onclick: (e: MouseEvent) => {
							e.stopPropagation() // cardul din jur are propriul click (deschide secțiunea)
							open_lightbox(poza, boxes)
						},
					}
				: {}),
		},
		t.img({
			src: `/api/support/help/shot/${encodeURIComponent(poza)}`,
			alt: "",
			loading: "lazy",
			class: "w-full rounded-lg border border-slate-200 dark:border-slate-700 block",
		}),
		...boxes.flatMap((b) => [
			t.span({
				class: "absolute border-2 border-amber-600 rounded-md pointer-events-none",
				style: `left:${b.l}%;top:${b.t}%;width:${b.w}%;height:${b.h}%`,
			}),
			t.span(
				{
					class:
						"absolute flex items-center justify-center w-4 h-4 rounded-full bg-amber-600 text-white text-[9px] font-bold pointer-events-none",
					style: `left:${b.l + b.w}%;top:${b.t}%;transform:translate(-100%,${b.t < 2.5 ? "5%" : "-105%"})`,
				},
				String(b.n),
			),
		]),
	)
}
/** HTML din atlas (sursă de încredere: generat din manuale) → innerHTML, sub stilurile .help-html. */
function html_block(html: string, cls = "") {
	const d = t.div({ class: `help-html text-sm text-slate-700 dark:text-slate-200 ${cls}` })
	d.innerHTML = html
	return d
}
function go_to(link: string): void {
	close_help()
	navigate(link)
}
function back_button(label = "← Înapoi") {
	return t.button(
		{
			class: BTN_GHOST,
			onclick: () => {
				S.view = S.thread ? "thread" : "home"
				S.error = ""
			},
		},
		label,
	)
}
function SectionView(sec: HelpSection) {
	const off = sec.module.length > 0
	return t.div(
		{ class: "space-y-2" },
		back_button(),
		t.h3({ class: "text-base font-bold text-slate-900 dark:text-slate-100" }, sec.titlu),
		t.div(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			`${sec.parte}${sec.unde && sec.unde !== "—" ? ` · ${sec.unde}` : ""}`,
			...(off
				? [
						t.span(
							{
								class:
									"ml-2 inline-flex rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px]",
							},
							`Modul: ${sec.flags.join(", ")}`,
						),
					]
				: []),
		),
		...(sec.poza ? [shot_view(sec.poza, sec.boxes)] : []),
		html_block(sec.text_html),
		...(sec.pasi_html.length
			? [
					t.ol(
						{
							class:
								"help-html list-decimal pl-5 space-y-1 text-sm text-slate-700 dark:text-slate-200",
						},
						...sec.pasi_html.map((p) => {
							const li = t.li()
							li.innerHTML = p
							return li
						}),
					),
				]
			: []),
		...sec.detaliu.map((d) =>
			t.div(
				{ class: "mt-2" },
				t.div(
					{ class: "text-[11px] text-slate-500 dark:text-slate-400" },
					`Detaliu mărit${d.legenda ? ` — ${d.legenda}` : ""}`,
				),
				shot_view(d.poza, d.boxes),
			),
		),
		...(sec.nota_html
			? [
					html_block(
						sec.nota_html,
						"rounded-lg bg-cyan-50 dark:bg-cyan-950/40 border-l-4 border-cyan-600 px-3 py-2 text-xs",
					),
				]
			: []),
		...(sec.link
			? [
					t.button(
						{ class: BTN + " mt-2", onclick: () => go_to(sec.link ?? "/") },
						`Deschide: ${sec.unde}`,
					),
				]
			: []),
	)
}
function ResultRow(r: HelpSearchResult) {
	return t.button(
		{
			class:
				"w-full text-left rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer",
			onclick: () => void open_section(r.id),
		},
		t.div(
			{ class: "flex items-center gap-2" },
			t.span({ class: "text-sm font-semibold text-slate-800 dark:text-slate-100" }, r.titlu),
			...(r.off_module
				? [
						t.span(
							{
								class:
									"inline-flex rounded-full bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-[10px] font-semibold",
							},
							"neinclus în contract",
						),
					]
				: []),
		),
		t.div(
			{ class: "text-[11px] text-slate-500 dark:text-slate-400" },
			`${r.parte}${r.unde && r.unde !== "—" ? ` · ${r.unde}` : ""}`,
		),
		t.div({ class: "text-xs text-slate-600 dark:text-slate-300 mt-0.5" }, r.snippet),
	)
}
/** Cardul compact al unei secțiuni citate de asistent: titlu + captură + „Deschide” + „Detalii”. */
function SectionCard(sec: HelpSection) {
	return t.div(
		{ class: CARD + " p-2.5 space-y-1.5" },
		t.div(
			{ class: "flex items-start justify-between gap-2" },
			t.div(
				t.div(
					{ class: "text-sm font-semibold text-slate-800 dark:text-slate-100 leading-snug" },
					sec.titlu,
				),
				t.div({ class: "text-[11px] text-slate-500 dark:text-slate-400" }, sec.parte),
			),
			// (pastila „modul” de pe card a fost scoasă 07.10: fără nume nu spunea nimic; dependența de modul se
			// vede în secțiunea deschisă — „Modul: …” — și prin marcajul „neinclus în contract” din căutare)
		),
		...(sec.poza ? [shot_view(sec.poza, sec.boxes, true)] : []),
		t.div(
			{ class: "flex items-center gap-2 flex-wrap" },
			...(sec.link
				? [
						t.button(
							{ class: BTN + " text-xs py-1", onclick: () => go_to(sec.link ?? "/") },
							`Deschide: ${sec.unde}`,
						),
					]
				: []),
			t.button(
				{ class: BTN_GHOST + " text-xs py-1", onclick: () => void open_section(sec.id) },
				"Pașii și detaliile →",
			),
		),
	)
}
/** Cardurile secțiunilor citate — încărcate imperativ într-un container (fără stare reactivă per card). */
function section_cards(ids: string[]) {
	const box = t.div({ class: "mt-2 space-y-2" })
	void Promise.all(ids.map(ensure_section)).then((secs) => {
		for (const sec of secs) {
			if (sec) {
				van.add(box, SectionCard(sec))
			}
		}
	})
	return box
}
function SearchBlock() {
	const input = t.input({
		type: "search",
		class: INPUT,
		placeholder: "Caută în ghid: „cum public harta”, „export shapefile”…",
		value: search_q,
		oninput: (e: Event) => {
			search_q = (e.target as HTMLInputElement).value
			if (search_timer) {
				clearTimeout(search_timer)
			}
			search_timer = setTimeout(() => run_search(search_q), 250)
		},
	})
	return t.div(
		{ class: "space-y-2" },
		t.div(
			{ class: "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400" },
			"Caută în ghid",
		),
		input,
		t.div({ class: "space-y-1.5" }, () =>
			S.results.length
				? t.div({ class: "space-y-1.5" }, ...S.results.map(ResultRow))
				: t.div(
						{ class: "text-xs text-slate-400 dark:text-slate-500" },
						S.searched ? "Nimic găsit în ghid. Scrie-ne mai jos." : "",
					),
		),
	)
}
function ThreadRow(th: SupportThread) {
	return t.button(
		{
			class:
				"w-full text-left rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer",
			onclick: () => void open_thread(th.id),
		},
		t.div(
			{ class: "flex items-center justify-between gap-2" },
			t.span(
				{ class: "text-sm font-medium text-slate-800 dark:text-slate-100 truncate" },
				th.subject,
			),
			status_badge(th.status),
		),
		t.div(
			{ class: "text-[11px] text-slate-500 dark:text-slate-400" },
			`${SUPPORT_KIND_LABEL_RO[th.kind] ?? th.kind} · ${fmt_date(th.updated_at)}`,
		),
	)
}
/** Explicația de sub formular: ce se întâmplă cu mesajul (cu sau fără asistent). */
/** Unde vine răspunsul: panoul mereu; emailul doar dacă instanța îl trimite (SUPPORT_MAIL_USERS). */
function answer_channel(): string {
	return S.ai.mail_users
		? "Primești răspunsul aici și pe email."
		: "Primești răspunsul aici, în panoul „Ajutor”."
}
function intro_text(): string {
	if (!S.ai.loaded) {
		return ""
	}
	return S.ai.assistant
		? `Asistentul răspunde din documentația platformei, cu capturi din manual. După al ${S.ai.max_replies}-lea răspuns fără rezolvare, conversația ajunge la echipa de suport, împreună cu pagina pe care ești și ultimele erori tehnice. Conversația cu asistentul e vizibilă echipei de suport și înainte de asta. Nu scrie date personale ale cetățenilor.`
		: "Mesajul ajunge direct la echipa de suport a platformei, împreună cu pagina pe care ești și ultimele erori tehnice. Nu scrie date personale ale cetățenilor."
}
function NewMessageBlock() {
	const ta = t.textarea({
		class: INPUT + " min-h-[5rem]",
		placeholder: "Descrie problema: ce încercai să faci, pe ce ai apăsat, ce s-a întâmplat…",
		maxlength: String(MAX_SUPPORT_BODY),
		value: draft_new,
		oninput: (e: Event) => {
			draft_new = (e.target as HTMLTextAreaElement).value
		},
	})
	return t.div(
		{ class: "space-y-2" },
		t.div(
			{ class: "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400" },
			() => (S.ai.assistant ? "Întreabă asistentul sau scrie echipei" : "Scrie echipei de suport"),
		),
		ta,
		t.p({ class: "text-[11px] text-slate-500 dark:text-slate-400 leading-snug" }, () =>
			intro_text(),
		),
		t.div(
			{ class: "flex items-center gap-2" },
			t.button({ class: BTN, disabled: () => S.busy, onclick: () => void send_new() }, () =>
				S.ai.assistant ? "Trimite" : "Trimite echipei",
			),
			t.span({ class: () => (S.busy ? "text-xs text-slate-400 animate-pulse" : "hidden") }, () =>
				S.ai.assistant ? "Asistentul scrie…" : "Se trimite…",
			),
		),
	)
}
function message_row(m: SupportMessage) {
	if (m.author_role === "system") {
		return t.div(
			{ class: "text-center text-[11px] text-slate-400 dark:text-slate-500 py-1" },
			`${m.body} · ${fmt_date(m.created_at)}`,
		)
	}
	const mine = m.author_role === "user"
	const assistant = m.author_role === "assistant"
	const sections =
		assistant && Array.isArray((m.meta as { sections?: unknown } | null)?.sections)
			? ((m.meta as { sections: string[] }).sections.filter(
					(x) => typeof x === "string",
				) as string[])
			: []
	return t.div(
		{ class: `flex ${mine ? "justify-end" : "justify-start"}` },
		t.div(
			{
				class:
					"max-w-[88%] rounded-2xl px-3.5 py-2 " +
					(mine
						? "bg-cyan-700 text-white"
						: assistant
							? "bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800 text-slate-800 dark:text-slate-200"
							: "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200"),
			},
			t.div(
				{
					class: `text-[11px] font-semibold mb-0.5 ${mine ? "text-cyan-100" : assistant ? "text-violet-700 dark:text-violet-300" : "text-slate-500 dark:text-slate-400"}`,
				},
				assistant ? "🤖 Asistent" : m.author_label,
			),
			t.div({ class: "text-sm whitespace-pre-wrap break-words" }, m.body),
			...(sections.length ? [section_cards(sections)] : []),
			t.div(
				{
					class: `text-[10px] mt-1 ${mine ? "text-cyan-200" : "text-slate-400 dark:text-slate-500"}`,
				},
				fmt_date(m.created_at),
			),
		),
	)
}
/** Textul de sub câmpul de răspuns, după starea firului. */
function state_text(th: SupportThread): string {
	if (awaiting_resolution(th)) {
		return "Asistentul a dat deja trei răspunsuri. Spune-ne dacă s-a rezolvat; altfel, la următorul mesaj firul ajunge la echipa de suport."
	}
	switch (th.status) {
		case "chat":
			return `Asistentul răspunde din documentația platformei (${th.ai_replies} din ${S.ai.max_replies} răspunsuri). Dacă nu rezolvă, firul ajunge la echipa de suport.`
		case "answered":
			return "Echipa a răspuns. Un mesaj nou trimite firul înapoi la echipă."
		case "closed":
			return "Firul e închis. Un mesaj nou îl redeschide."
		default:
			return `Firul e la echipa de suport. ${answer_channel()}`
	}
}
function ThreadView(d: SupportThreadDetail) {
	const th = d.thread
	const waiting = awaiting_resolution(th)
	const ta = t.textarea({
		class: INPUT + " min-h-[4rem]",
		placeholder:
			th.status === "closed"
				? "Scrie ca să redeschizi firul…"
				: th.status === "chat"
					? "Întreabă mai departe sau descrie ce lipsește…"
					: "Răspunde…",
		maxlength: String(MAX_SUPPORT_BODY),
		value: draft_reply,
		oninput: (e: Event) => {
			draft_reply = (e.target as HTMLTextAreaElement).value
		},
	})
	// firul se deschide DERULAT la capăt: ultimul mesaj și caseta de scris la vedere (testare 07.10: după trei
	// răspunsuri cu carduri, caseta ajungea sub pliu); microtask = după ce legătura părinte a atașat nodul
	queueMicrotask(() => ta.scrollIntoView({ block: "end" }))
	return t.div(
		{ class: "space-y-3" },
		t.div(
			{ class: "flex items-center justify-between gap-2" },
			back_home_button(),
			status_badge(th.status),
		),
		t.h3(
			{ class: "text-base font-bold text-slate-900 dark:text-slate-100 leading-snug" },
			th.subject,
		),
		t.div(
			{ class: "text-[11px] text-slate-500 dark:text-slate-400" },
			`${SUPPORT_KIND_LABEL_RO[th.kind] ?? th.kind} · deschis ${fmt_date(th.created_at)}${th.assigned_label ? ` · preluat de ${th.assigned_label}` : ""}`,
		),
		t.div(
			{ class: "space-y-2 rounded-xl bg-slate-50 dark:bg-slate-950 p-3" },
			...d.messages.map(message_row),
		),
		t.div(
			{
				class: () =>
					S.notice
						? "text-xs rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 px-3 py-2"
						: "hidden",
			},
			() => S.notice,
		),
		// al 3-lea răspuns: întrebarea explicită, cu cele două butoane
		...(waiting
			? [
					t.div(
						{
							class:
								"flex items-center gap-2 flex-wrap rounded-lg bg-violet-50 dark:bg-violet-950/40 px-3 py-2",
						},
						t.span({ class: "text-xs text-slate-700 dark:text-slate-200" }, "S-a rezolvat?"),
						t.button(
							{
								class: BTN + " text-xs py-1",
								disabled: () => S.busy,
								onclick: () => void resolve_current(),
							},
							"✓ Da, s-a rezolvat",
						),
						t.button(
							{
								class: BTN_GHOST + " text-xs py-1",
								disabled: () => S.busy,
								onclick: () => void escalate_current(),
							},
							"Nu, trimite echipei",
						),
					),
				]
			: []),
		ta,
		t.div(
			{ class: "flex items-center gap-2 flex-wrap" },
			t.button({ class: BTN, disabled: () => S.busy, onclick: () => void send_reply() }, "Trimite"),
			...(th.status !== "closed" && !waiting
				? [
						t.button(
							{ class: BTN_GHOST, disabled: () => S.busy, onclick: () => void resolve_current() },
							"✓ S-a rezolvat",
						),
					]
				: []),
			...(th.status === "chat"
				? [
						t.button(
							{ class: BTN_GHOST, disabled: () => S.busy, onclick: () => void escalate_current() },
							"Vreau să vorbesc cu cineva",
						),
					]
				: []),
			t.span({ class: () => (S.busy ? "text-xs text-slate-400 animate-pulse" : "hidden") }, () =>
				th.status === "chat" && S.ai.assistant ? "Asistentul scrie…" : "Se trimite…",
			),
		),
		t.p({ class: "text-[11px] text-slate-500 dark:text-slate-400" }, state_text(th)),
	)
}
function back_home_button() {
	return t.button(
		{
			class: BTN_GHOST,
			onclick: () => {
				S.view = "home"
				S.thread = null
				S.error = ""
				S.notice = ""
			},
		},
		"← Înapoi",
	)
}
/** Câte fire se arată acasă — restul pe pagina #/suport (testare 07.10: cu 6+ fire caseta de scris ajungea sub pliu). */
const HOME_THREADS = 5
function HomeView(all_threads = false) {
	// în panou lista e plafonată (HOME_THREADS + link); pe pagina #/suport se arată TOATE firele (testare 08.10)
	const limit = all_threads ? S.threads.length : HOME_THREADS
	return t.div(
		{ class: "space-y-5" },
		SearchBlock(),
		// caseta de scris stă DEASUPRA listei de fire: e acțiunea principală (chat-first) și nu trebuie
		// căutată cu scroll după ce se adună firele
		NewMessageBlock(),
		t.div(
			{ class: "space-y-2" },
			t.div(
				{
					class: "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400",
				},
				"Firele tale",
			),
			t.div({ class: "space-y-1.5" }, () =>
				S.threads.length
					? t.div(
							{ class: "space-y-1.5" },
							...S.threads.slice(0, limit).map(ThreadRow),
							...(!all_threads && S.threads.length > HOME_THREADS
								? [
										t.a(
											{
												href: "#/suport",
												class:
													"block text-xs text-cyan-700 dark:text-cyan-300 hover:underline pt-1",
											},
											`Toate firele (${S.threads.length}) →`,
										),
									]
								: []),
						)
					: t.div(
							{ class: "text-xs text-slate-400 dark:text-slate-500" },
							S.threads_loaded ? "Niciun fir deschis încă." : "Se încarcă…",
						),
			),
		),
	)
}
/** Corpul panoului/paginii: comută între acasă, secțiune și fir. Construit o dată per montare. */
function Body(all_threads = false) {
	return t.div(
		{ class: "space-y-3" },
		t.div(
			{
				class: () =>
					S.error
						? "text-xs rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 px-3 py-2"
						: "hidden",
			},
			() => S.error,
		),
		t.div(() => {
			if (S.view === "section" && S.section) {
				return SectionView(S.section)
			}
			if (S.view === "thread" && S.thread) {
				return ThreadView(S.thread)
			}
			return HomeView(all_threads)
		}),
	)
}

// ── panoul (montat o dată) ────────────────────────────────────────────────────────────────────
let panel_mounted = false
export function mount_help_panel(): void {
	if (panel_mounted || typeof document === "undefined") {
		return
	}
	panel_mounted = true
	const scrim = t.div({
		class: () => "fixed inset-0 z-40 bg-black/30" + (S.open ? "" : " hidden"),
		onclick: close_help,
	})
	const drawer = t.aside(
		{
			class: () =>
				"fixed z-50 bg-white dark:bg-slate-900 shadow-2xl flex flex-col " +
				"inset-x-0 bottom-0 max-h-[88dvh] rounded-t-2xl border-t border-slate-200 dark:border-slate-700 " +
				"md:inset-y-0 md:right-0 md:left-auto md:w-[26rem] md:max-h-none md:rounded-none md:border-t-0 md:border-l" +
				(S.open ? "" : " hidden"),
			role: "dialog",
			"aria-label": "Ajutor",
		},
		t.div(
			{
				class:
					"flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700",
			},
			t.div(
				t.div({ class: "text-sm font-bold text-slate-900 dark:text-slate-100" }, "Ajutor"),
				t.div({ class: "text-[11px] text-slate-500 dark:text-slate-400" }, () =>
					S.ai.assistant
						? "asistentul, ghidul platformei și echipa de suport"
						: "ghidul platformei și echipa de suport",
				),
			),
			t.button(
				{
					class: BTN_GHOST + " text-lg leading-none",
					"aria-label": "Închide",
					onclick: close_help,
				},
				"✕",
			),
		),
		t.div(
			{ class: "flex-1 overflow-y-auto px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]" },
			Body(),
		),
	)
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && S.open) {
			close_help()
		}
	})
	document.body.append(scrim, drawer)
}

/** Butonul din antet (desktop: text; mobil: doar simbolul). Vizibil doar pentru conturile de lucru. */
export function HelpButton() {
	return t.button(
		{
			class: () =>
				has_work_identity()
					? "flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer flex-shrink-0"
					: "hidden",
			"aria-label": "Ajutor",
			onclick: () => open_help(),
		},
		t.span(
			{
				class:
					"inline-flex items-center justify-center w-4 h-4 rounded-full bg-cyan-700 text-white text-[10px] font-bold",
			},
			"?",
		),
		t.span({ class: "hidden md:inline" }, "Ajutor"),
	)
}

/** Pagina `#/suport` (deep-link din email: `?t=<id>`): același conținut, în pagină. */
export function SupportPage(): HTMLElement {
	void load_config()
	if (!S.threads_loaded) {
		void load_threads()
	}
	const want = get_hash_query_params().get("t")
	if (want) {
		void open_thread(want)
	} else {
		S.view = "home"
	}
	return t.div(
		{ class: "max-w-3xl mx-auto px-4 py-8" },
		t.h1(
			{ class: "text-2xl font-extrabold text-slate-900 dark:text-slate-100 mb-1" },
			"Ajutor și suport",
		),
		t.p(
			{ class: "text-sm text-slate-500 dark:text-slate-400 mb-6" },
			"Caută în ghidul platformei sau scrie echipei de suport. Firele tale rămân aici, cu răspunsurile.",
		),
		t.div({ class: CARD + " p-5" }, Body(true)),
	)
}
